/** A room's Announcements: only Maya posts; everyone registered sees it, emailed unless she turns it off. */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import type { TalkAnnouncement } from "../../shared/talk";
import type { Attachment } from "../../shared/types";
import { Button } from "../components/ui/Button";
import { FaceStack } from "../components/ui/Face";
import { Sheet } from "../components/ui/Sheet";
import { useToast } from "../components/ui/Toast";
import { api } from "../lib/api";
import { useHost } from "./useHost";
import { useStick } from "./Chat";
import { RoomSide } from "./Everyone";
import { Composer } from "./Composer";
import { facesOf, Head, PostCard, Poster } from "./parts";
import { Nav } from "./phone";
import { directListPath, directPath } from "./places";
import { useTalkContext } from "./TalkLayout";
import { useRemote } from "./useTalk";
import { peopleWords } from "./when";

/** The latest post: how many saw it and who answered. */
function LatestCard({ last, people, onDirect }: { last: TalkAnnouncement; people: number; onDirect: () => void }) {
  const seen = last.seen ?? 0;
  const replied = last.repliedBy ?? [];
  return (
    <div className="side-card">
      <small>The latest post</small>
      <b>
        Seen by {seen.toLocaleString("en-US")} of {people.toLocaleString("en-US")}
      </b>
      <span className="seenbar">
        <i style={{ width: `${people ? Math.round((seen / people) * 100) : 0}%` }} />
      </span>
      {replied.length ? (
        <span className="side-card-who">
          <FaceStack people={facesOf(replied.slice(0, 4))} size={24} more={Math.max(0, (last.replied ?? replied.length) - 4)} />
          <em>{last.replied ?? replied.length} replied to you</em>
        </span>
      ) : null}
      <Button size="s" icon="chat" onClick={onDirect}>
        Open in Direct
      </Button>
    </div>
  );
}

/** Opens Direct for the people who answered: the first person waiting, or the Direct list. */
function useOpenReplies() {
  const { rail, lay } = useTalkContext();
  const navigate = useNavigate();
  return () => {
    const first = rail?.direct.find((r) => r.unread) ?? rail?.direct[0];
    navigate(lay === "phone" || !first ? directListPath : directPath(first.contactId));
  };
}

export function AnnouncementsScreen() {
  const { wid = "" } = useParams();
  const ctx = useTalkContext();
  const phone = ctx.lay === "phone";
  const upright = ctx.lay === "upright";
  const toast = useToast();
  const openReplies = useOpenReplies();
  const { data, phase, reload } = useRemote(`talk:announce:${wid}`, () => api.talk.announcements(wid), `/talk/rooms/${encodeURIComponent(wid)}/ws`);
  const host = useHost();
  const chatOff = !!data && !data.room.everyoneChat;
  const room = useRemote(chatOff ? `talk:everyone:${wid}` : null, () => api.talk.everyone(wid));
  const [email, setEmail] = useState(true);
  const [pin, setPin] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [info, setInfo] = useState(false);
  const scroll = useRef<HTMLDivElement>(null);
  const posts = (data?.posts ?? []).toSorted((a, b) => a.createdAt - b.createdAt);
  const onScroll = useStick(scroll, posts.length, wid);
  useEffect(() => {
    if (data) ctx.reloadRail();
  }, [data, ctx.reloadRail]);

  if (!data) return phase === "error" ? <main className="main"><p className="empty-note">We couldn't open the announcements. Pick them again.</p></main> : <main className="main" />;
  const last = posts.at(-1);
  const people = data.people;

  const post = async (out: { text: string; attachment: Attachment | null }) => {
    setNotice(null);
    try {
      await api.talk.announce(wid, { body: out.text, attachments: out.attachment ? [out.attachment] : undefined, email, pin, requestId: crypto.randomUUID() });
      setPin(false);
      setEmail(true);
      toast({ text: email ? "Posted and emailed" : "Posted", icon: "send" });
      await reload();
    } catch (err) {
      setNotice(err instanceof Error && err.message ? err.message : "We couldn't post that. Try again.");
      throw err;
    }
  };
  const host2 = { name: host.name, photo: host.photo };
  const composer = (
    <Composer
      to={`all ${people.toLocaleString("en-US")}`}
      phone={phone}
      host={host2}
      send="Post"
      placeholder={phone ? `Write to all ${people.toLocaleString("en-US")}` : `Write to all ${people.toLocaleString("en-US")} in the room...`}
      onSend={post}
      notice={notice}
      checks={
        phone ? (
          <label className="hp-check">
            <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} /> Also email it
          </label>
        ) : (
          <>
            <label className="cmp-pin">
              <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} /> Also email it
            </label>
            <label className="cmp-pin">
              <input type="checkbox" checked={pin} onChange={(e) => setPin(e.target.checked)} /> Pin it
            </label>
          </>
        )
      }
    />
  );
  const cards = posts.map((p) => <PostCard key={p.id} p={p} host={host2} onReplies={openReplies} />);
  // Why: a room with Everyone chat off keeps its settings and groups here; one with chat on shows how the last post did.
  const sideBody = chatOff ? room.data ? <RoomSide wid={wid} data={room.data} onChange={() => void room.reload()} /> : null : last ? <LatestCard last={last} people={people} onDirect={openReplies} /> : null;
  const inline = upright && chatOff;

  if (phone) {
    return (
      <div className="hp">
        <Nav back="Rooms" title="Announcements" sub={data.room.title} />
        <div className="hp-chat posts" ref={scroll} onScroll={onScroll}>
          {cards}
        </div>
        {composer}
      </div>
    );
  }
  return (
    <>
      <main className="main">
        <Head
          pic={<Poster url={data.room.posterUrl} className="mh-pic" />}
          title="Announcements"
          sub={`${data.room.title} · ${peopleWords(people)}`}
          onInfo={upright && !chatOff ? () => setInfo(true) : undefined}
          infoLabel="About this room"
        />
        <div className="chat posts" ref={scroll} onScroll={onScroll}>
          {inline ? <div className="side-card inmain">{sideBody}</div> : null}
          {cards}
        </div>
        {composer}
      </main>
      <aside className="side">{sideBody}</aside>
      <Sheet open={info} onClose={() => setInfo(false)} title={data.room.title}>
        <div className="sheet-b side-sheet">{sideBody}</div>
      </Sheet>
    </>
  );
}
